import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import useCart from '../../data/useCart'
import useCheckoutContext from '../../data/useCheckoutContext'
import useMutationWithErrorHandling from '../../data/useMutationWithErrorHandling'
import getPhoenixUrlParams from '../../lib/get-phoenix-url-params'
import { ProductSelectionInitialValues } from '../../typings/Product'

import {
  ProductSelectionCartQuery,
  ProductSelectionCartDocument,
  ProductSelectionSetProductForCartItemDocument,
  ProductSelectionSetProductForCartItemMutation,
  ProductSelectionSetProductForCartItemMutationVariables,
} from '../../graphql'

import ProductSelectionComponent from './ProductSelectionComponent'
import SkeletonProductionSelection from './Skeletons/SkeletonProductSelection'

const ProductSelection: React.FC<React.PropsWithChildren<unknown>> = () => {
  const location = useLocation()
  const context = useCheckoutContext()
  const { cartId } = getPhoenixUrlParams(location.search)

  const [initialValues, setInitialValues] = useState<ProductSelectionInitialValues>()

  const { cartData, isCartLoading } = useCart<ProductSelectionCartQuery>(ProductSelectionCartDocument)

  const [setProductForCartItem, { data: setProductForCartItemData, loading: setProductForCartItemDataLoading }] =
    useMutationWithErrorHandling<
      ProductSelectionSetProductForCartItemMutation,
      ProductSelectionSetProductForCartItemMutationVariables
    >(ProductSelectionSetProductForCartItemDocument, {})

  const handleProductSelection = (productId: string) => {
    if (!cartData) return
    const itemId = cartData.cart.items[0].id

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    setProductForCartItem({ variables: { cartId: cartId as string, itemId, productId } })
    context.setIsSummaryLoading(true)
  }

  useEffect(() => {
    if (setProductForCartItemDataLoading || !setProductForCartItemData) {
      context.setIsProductSelectionValid(false)
    } else if (setProductForCartItemData && setProductForCartItemData.setProductForCartItem.__typename === 'Cart') {
      // Set the product selection validity to true after we get back the result from the mutation
      context.setIsProductSelectionValid(true)
      context.setIsSummaryLoading(false)
    }
  }, [setProductForCartItemDataLoading, setProductForCartItemData])

  useEffect(() => {
    if (!cartData) return

    const assignedProducts = cartData.cart.items[0].confirmWithOptions
    const settleWith = cartData.cart.items[0].settleWith && cartData.cart.items[0].settleWith
    const firstValidAssignedProduct = cartData.cart.items[0].confirmWithOptions.find(
      (item) => item.assignedProductValidity.isValid,
    )

    if (!settleWith && assignedProducts.length > 0 && firstValidAssignedProduct) {
      const itemId = cartData.cart.items[0].id

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      setProductForCartItem({
        variables: { cartId: cartId as string, itemId, productId: firstValidAssignedProduct.id },
      })
      setInitialValues({ settleWithId: firstValidAssignedProduct.id })
      context.setIsProductSelectionValid(true)
    } else {
      const settleWithInitialValue = settleWith ? settleWith.id : ''
      setInitialValues({ settleWithId: settleWithInitialValue })

      if (settleWithInitialValue) {
        context.setIsProductSelectionValid(true)
      }
    }
  }, [cartData])

  if (!cartData || isCartLoading || !initialValues) {
    return <SkeletonProductionSelection />
  }

  return (
    <ProductSelectionComponent
      initialValues={initialValues}
      purchaseOptions={cartData.cart.items[0].purchaseOptions}
      assignedProducts={cartData.cart.items[0].confirmWithOptions}
      onProductSelection={handleProductSelection}
    />
  )
}

export default ProductSelection
